import {
  LS_SESSION_ID,
  LS_USER,
  LS_USERNAME,
  LS_COMPANY_PRIVILEGES,
} from "../constants/storageKeys";

/**
 * Why the user is being sent to the login page (7HAI-1817). The login page
 * reads it from `?reason=` and says so, instead of greeting someone whose
 * session just ended as if they had never signed in.
 */
export type SessionEndReason = "expired" | "signedOut";

/**
 * End the session on this device: forget the cached session and go to the
 * login page with the reason. One implementation for every path that ends a
 * session — a 401 from any request, the session watcher, a sign-out in another
 * tab — so they can never drift apart.
 *
 * Idempotent: on the login page already it does nothing, so the login form's
 * own "wrong password" 401 never bounces.
 */
/**
 * Remove the API response cache an older service worker left on disk
 * (7HAI-2083): the current build no longer writes one, but a profile upgraded
 * from before still holds the previous user's responses. Best effort — the
 * Cache API is absent in some contexts and may throw in private mode.
 */
export async function dropApiCache(): Promise<void> {
  try {
    if (typeof caches !== "undefined") await caches.delete("api-get-cache");
  } catch {
    /* ignore */
  }
}

export function endSession(reason: SessionEndReason): void {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/login") return;
  try {
    localStorage.removeItem(LS_SESSION_ID);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_USERNAME);
    localStorage.removeItem(LS_COMPANY_PRIVILEGES);
  } catch {
    /* localStorage can throw in private mode — ignore */
  }
  void dropApiCache();
  // Hard navigation, so every mounted component and its cached state goes;
  // `replace` keeps the protected URL out of history so Back cannot return.
  window.location.replace(`/login?reason=${reason}`);
}
